import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  TrendingUp,
  Compass,
  CheckCircle2,
  ArrowRight,
  Search,
  LayoutDashboard,
  Quote,
  ClipboardList,
} from "lucide-react";

const profileParts = ["Interesses verkend", "Talenten beschreven", "Ervaring gereflecteerd", "Volgende stap gekozen"];

const options = ["Toegepaste Psychologie", "Communicatie & Media", "Sociaal Werk"];

const tabs = [
  {
    id: "ontdekken",
    label: "Ontdekken",
    icon: Search,
    title: "Leer de leerling écht kennen.",
    text: "Opdrachten, reflecties, gesprekken en waar relevant testen helpen een leerling zichzelf steeds beter te leren kennen.",
    to: "/lob-reis",
    cta: "Bekijk de LOB-reis",
    note: "Het profiel groeit mee met wat een leerling doet, ervaart en bespreekt.",
  },
  {
    id: "kiezen",
    label: "Kiezen",
    icon: Sparkles,
    title: "Van zelfkennis naar een passende keuze.",
    text: "De leerling verkent en vergelijkt opleidingen vanuit het opgebouwde profiel, met uitleg en ruimte om zelf verder te onderzoeken.",
    to: "/keuzehulp",
    cta: "Bekijk de keuzehulp",
    note: "Suggesties geven richting; de leerling onderzoekt, vergelijkt en beslist zelf.",
  },
  {
    id: "begeleiden",
    label: "Begeleiden",
    icon: LayoutDashboard,
    title: "Grip op iedere leerling.",
    text: "Voortgang, reflecties en aandachtspunten komen samen in één overzicht. Zo begint een mentor elk gesprek met meer context.",
    to: "/dashboard",
    cta: "Ontdek het dashboard",
    note: "Het overzicht ondersteunt het gesprek; persoonlijke begeleiding blijft mensenwerk.",
  },
];

const PlatformShowcaseSection = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(tabs[0].id);
  const tab = tabs.find((t) => t.id === active) ?? tabs[0];

  return (
    <section className="relative py-20 lg:py-28 animate-on-scroll overflow-hidden">
      <div className="pointer-events-none absolute -top-16 right-0 w-[480px] h-[480px] rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-warm/10 blur-3xl" />

      <div className="container mx-auto max-w-6xl px-4 sm:px-6 relative">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-primary mb-4 px-3 py-1 bg-accent rounded-full">
            <Sparkles className="w-3.5 h-3.5" />
            Het platform
          </span>
          <h2 className="text-3xl lg:text-[2.6rem] font-extrabold text-secondary leading-tight">
            Eén platform, de hele reis van de leerling.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Van ontdekken tot kiezen tot begeleiden — alles werkt samen.
          </p>
        </div>

        {/* tab switcher */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                active === t.id
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-center">
          {/* mockup */}
          <div className="rounded-[1.75rem] border border-border bg-card shadow-large overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border bg-muted/50">
              <span className="w-3 h-3 rounded-full bg-destructive/40" />
              <span className="w-3 h-3 rounded-full bg-warm/50" />
              <span className="w-3 h-3 rounded-full bg-primary/40" />
              <div className="ml-4 flex-1 max-w-xs">
                <div className="h-6 rounded-full bg-background border border-border flex items-center px-3 text-[11px] text-muted-foreground">
                  portal.studie-connect.nl
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 bg-gradient-to-br from-background to-accent/30 min-h-[360px]">
              {active === "ontdekken" && (
                <div className="rounded-2xl border border-border bg-card p-5 shadow-soft animate-fade-in">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center font-extrabold text-primary">
                      SV
                    </div>
                    <div>
                      <p className="font-bold text-secondary leading-tight">Sanne van Dijk</p>
                      <p className="text-xs text-muted-foreground">6 vwo · Leerjaar 6</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <p className="text-sm font-bold text-secondary">Mijn groeiende profiel</p>
                  </div>
                  <div className="space-y-3">
                    {profileParts.map((label, index) => (
                      <div key={label} className="flex items-center gap-3 rounded-xl bg-accent/50 px-3 py-2.5">
                        {index < 3 ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <ClipboardList className="h-4 w-4 text-warm" />}
                        <span className="text-xs font-semibold text-secondary">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {active === "kiezen" && (
                <div className="rounded-2xl bg-gradient-to-br from-primary to-primary-hover p-6 text-primary-foreground shadow-medium animate-fade-in">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4" />
                    <p className="text-sm font-bold">Opleidingen om te onderzoeken</p>
                  </div>
                  <p className="text-sm text-primary-foreground/85 mb-4 leading-relaxed">
                    Deze opleidingen sluiten aan op delen van je profiel. Bekijk waarom en vergelijk zelf verder.
                  </p>
                  <div className="space-y-2.5">
                    {options.map((name, index) => (
                      <div
                        key={name}
                        className="flex items-center justify-between bg-primary-foreground/10 rounded-xl px-4 py-3 backdrop-blur"
                      >
                        <span className="text-sm font-semibold">{name}</span>
                        <span className="text-xs font-bold bg-primary-foreground/20 rounded-full px-2.5 py-1">
                          Optie {index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {active === "begeleiden" && (
                <div className="space-y-4 animate-fade-in">
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: "Op koers", value: "68%", tone: "text-primary" },
                      { label: "Aandacht", value: "24%", tone: "text-warm" },
                      { label: "Achter", value: "8%", tone: "text-destructive" },
                    ].map((s) => (
                      <div key={s.label} className="rounded-xl border border-border bg-card p-4 text-center shadow-soft">
                        <div className={`text-2xl font-extrabold ${s.tone}`}>{s.value}</div>
                        <div className="text-[11px] text-muted-foreground font-medium mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                    <div className="flex items-center gap-2 mb-4">
                      <Compass className="w-4 h-4 text-primary" />
                      <p className="text-sm font-bold text-secondary">Signalen deze week</p>
                    </div>
                    <ul className="space-y-2.5">
                      {[
                        { text: "Lisa – reflectie 2 weken open", done: false },
                        { text: "Groep 4H2 – testen afgerond", done: true },
                        { text: "Omar – keuze vastgelegd", done: true },
                      ].map((s) => (
                        <li key={s.text} className="flex items-center gap-2.5 text-sm">
                          <CheckCircle2
                            className={`w-4 h-4 shrink-0 ${
                              s.done ? "text-primary" : "text-muted-foreground/40"
                            }`}
                          />
                          <span className={s.done ? "text-secondary font-semibold" : "text-muted-foreground"}>
                            {s.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* copy + quote */}
          <div className="animate-fade-in" key={tab.id}>
            <h3 className="text-2xl lg:text-3xl font-extrabold text-secondary leading-tight mb-4">
              {tab.title}
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">{tab.text}</p>

            <Button
              onClick={() => navigate(tab.to)}
              variant="outline"
              className="rounded-full font-semibold gap-2 mb-8"
            >
              {tab.cta} <ArrowRight className="w-4 h-4" />
            </Button>

            <div className="rounded-2xl border border-border bg-accent/40 p-6">
              <Quote className="w-7 h-7 text-primary/40 mb-3" />
              <p className="text-secondary font-semibold leading-relaxed">{tab.note}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformShowcaseSection;
